import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Bell, Calendar, Lock } from 'lucide-react';
import { WEEK_FOCUSES } from '../utils/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleChangePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/settings`, settings);
      toast.success('Settings saved!');
      
      // Update theme
      localStorage.setItem('theme', settings.theme);
      document.documentElement.classList.toggle('dark', settings.theme === 'dark');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const weekInfo = WEEK_FOCUSES[settings.current_week];

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-2">Settings</h1>
        <p className="text-lg text-muted-foreground font-['IBM_Plex_Sans']">
          Customize your training experience
        </p>
      </div>

      {/* Week Selection */}
      <div className="mb-8 bg-card border border-border p-4 sm:p-8 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <Calendar className="w-5 h-5 text-primary" />
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Training Week</h2>
        </div>

        <div className="mb-6">
          <Label className="uppercase tracking-widest text-xs font-medium mb-3 block">
            Current Week
          </Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(week => {
              const info = WEEK_FOCUSES[week];
              return (
                <button
                  key={week}
                  data-testid={`btn-week-${week}`}
                  onClick={() => setSettings({ ...settings, current_week: week })}
                  className={`p-4 border rounded-sm transition-colors ${
                    settings.current_week === week
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="text-2xl font-bold tracking-tight mb-1">Week {week}</div>
                  <div className="text-xs font-['IBM_Plex_Sans']">{info.range}</div>
                </button>
              );
            })}
          </div>
        </div>

        {weekInfo && (
          <div className="p-4 bg-muted/50 rounded-sm">
            <p className="text-sm font-bold mb-1 font-['IBM_Plex_Sans']">{weekInfo.focus}</p>
            <p className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">
              {weekInfo.description}
            </p>
          </div>
        )}
      </div>

      {/* Theme Selection */}
      <div className="mb-8 bg-card border border-border p-4 sm:p-8 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <SettingsIcon className="w-5 h-5 text-primary" />
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Appearance</h2>
        </div>
        
        <Label className="uppercase tracking-widest text-xs font-medium mb-3 block">
          Theme
        </Label>
        <div className="grid grid-cols-2 gap-4">
          <button
            data-testid="btn-theme-light"
            onClick={() => setSettings({ ...settings, theme: 'light' })}
            className={`p-6 border rounded-sm transition-colors ${
              settings.theme === 'light'
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <div className="text-lg font-bold tracking-tight mb-2">The Newsroom</div>
            <div className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">
              Paper-white background, ideal for daytime writing
            </div>
          </button>

          <button
            data-testid="btn-theme-dark"
            onClick={() => setSettings({ ...settings, theme: 'dark' })}
            className={`p-6 border rounded-sm transition-colors ${
              settings.theme === 'dark'
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <div className="text-lg font-bold tracking-tight mb-2">The Late Shift</div>
            <div className="text-xs text-muted-foreground font-['IBM_Plex_Sans']">
              Dark mode for focused evening sessions
            </div>
          </button>
        </div>
      </div>

      {/* Reminders */}
      <div className="mb-8 bg-card border border-border p-4 sm:p-8 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <Bell className="w-5 h-5 text-primary" />
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Reminders</h2>
        </div>
        
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            data-testid="checkbox-reminders"
            type="checkbox"
            checked={settings.reminders_enabled}
            onChange={(e) => setSettings({ ...settings, reminders_enabled: e.target.checked })}
            className="w-5 h-5 rounded border-border"
          />
          <div>
            <div className="font-medium font-['IBM_Plex_Sans']">Enable daily reminders</div>
            <div className="text-sm text-muted-foreground font-['IBM_Plex_Sans']">
              Get notified to complete your daily training session
            </div>
          </div>
        </label>
      </div>

      {/* Change Password */}
      <div className="mb-8 bg-card border border-border p-4 sm:p-8 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <Lock className="w-5 h-5 text-primary" />
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Change Password</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="uppercase tracking-widest text-xs font-medium mb-2 block">
              Current Password
            </Label>
            <input
              type="password"
              value={pwForm.current_password}
              onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
              className="w-full border border-border rounded-sm px-3 py-2 bg-background text-sm font-['IBM_Plex_Sans']"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <Label className="uppercase tracking-widest text-xs font-medium mb-2 block">
              New Password
            </Label>
            <input
              type="password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
              className="w-full border border-border rounded-sm px-3 py-2 bg-background text-sm font-['IBM_Plex_Sans']"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <Label className="uppercase tracking-widest text-xs font-medium mb-2 block">
              Confirm New Password
            </Label>
            <input
              type="password"
              value={pwForm.confirm_password}
              onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
              className="w-full border border-border rounded-sm px-3 py-2 bg-background text-sm font-['IBM_Plex_Sans']"
              placeholder="Repeat new password"
            />
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={pwSaving || !pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password}
            className="uppercase tracking-wide font-medium"
          >
            {pwSaving ? 'Updating...' : 'Update Password'}
          </Button>
        </div>
      </div>

      {/* Save Button */}
      <Button
        data-testid="btn-save-settings"
        onClick={handleSave}
        disabled={saving}
        className="w-full uppercase tracking-wide font-medium"
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
}

